import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import _ from 'lodash';
import { Assumptions } from '../static/Assumptions';
import { Customer } from './Customer';

@supertypeClass({ toClient: true, toServer: true })
export class Phone extends Everything {
  static TYPES = { mobile: '12', home: '1', work: '2' };

  @property()
  bucket: string;

  /* Used temporarily to ensure master list in customer is accurate*/
  @property({
    rule: ['telephone'],
    values: function () {
      if (!this.customer) {
        return [];
      }

      return _.uniq(
        _.filter(
          _.map(
            this.customer.phones,
            function (phone) {
              if (phone.number) {
                return {
                  listValue: phone.getFormatted(phone.number).replace(/[()]/g, ''),
                  fieldValue: phone.getFormatted(phone.number).replace(/[()]/g, ''),
                  unformattedValue: phone.number,
                  type: phone.type,
                };
              } else {
                return null;
              }
            }.bind(this)
          ),
          function (o) {
            return !!o;
          }
        )
      );
    },
  })
  number: string;

  @property({
    rule: ['required'],
    values: function () {
      // Need to check for existence of this.customer as the
      // CAS's primaryPolicyAgent's phone doesn't have a backlink
      // to the customer
      const policy = this.customer && this.customer.application ? this.customer.application.primaryPolicy : null;
      const channelCode = policy ? policy.getProductInfo().channel.code : null;

      if ((policy && policy.isICCApplication()) || channelCode === 'CAS') {
        return ['1', '2', '12'];
      } else {
        return ['12', '1', '2'];
      }
    },
    descriptions: function () {
      const policy = this.customer && this.customer.application ? this.customer.application.primaryPolicy : null;
      const channelCode = policy ? policy.getProductInfo().channel.code : null;

      if ((policy && policy.isICCApplication()) || channelCode === 'CAS') {
        return { '1': 'Home', '2': 'Work', '12': 'Mobile' };
      } else {
        return { '1': 'Home', '2': 'Other', '12': 'Mobile' };
      }
    },
  })
  type: string;

  @property({ rule: ['required', 'text'], values: Assumptions.countryValues })
  country: string = 'US';

  @property({ getType: () => Customer })
  customer: Customer;

  constructor() {
    super();
  }

  static new(customer?, type?) {
    return new Phone().init(customer, type);
  }

  init(customer?, type?) {
    if (customer) {
      this.customer = customer;
      this.customer.phones.push(this);
    }

    this.type = type || Phone.TYPES.mobile;
    return this;
  }

  numberTypeaheadTrigger(obj) {
    this.number = obj.unformattedValue;
    this.type = obj.type;
  }

  typeTrigger() {
    this.type = this.type || Phone.TYPES.mobile;
  }

  countryTrigger() {
    if (!this.country) {
      this.country = 'US';
    }
  }

  getAreaCode() {
    return this.number && this.number.length >= 10 ? this.number.substr(0, 3) : '';
  }

  getExchange() {
    return this.number && this.number.length >= 10 ? this.number.substr(3, 3) : '';
  }

  getLine() {
    return this.number && this.number.length >= 10 ? this.number.substr(6, 4) : '';
  }

  getExtension() {
    return this.number && this.number.length > 10 && Number(this.number.substr(10)) !== 0 ? this.number.substr(10) : '';
  }

  getFormatted(digits?) {
    digits = digits || this.number;

    if (!digits) {
      return '';
    }

    const parts = digits.split(' ');

    if (parts.length < 2 || parts[0].length < 10) {
      return digits;
    }

    return (
      '(' +
      parts[0].substr(0, 3) +
      ') ' +
      parts[0].substr(3, 3) +
      '-' +
      parts[0].substr(6, 4) +
      (parts[1] > 0 ? ' x' + parts[1] : '')
    );
  }

  toString() {
    if (!this.number) {
      return '';
    }

    return this.getFormatted();
  }

  getChooserDisplay() {
    return this.toString();
  }

  isMobile() {
    return this.type === '12';
  }

  isHome() {
    return this.type === '1';
  }

  isWork() {
    return this.type === '2';
  }
}
